/**
 * Date utility functions using date-fns
 * Handles date manipulation, formatting, and day generation for trips
 */

import { format, addDays as addDaysFns, differenceInDays, differenceInHours, isPast } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import type { Trip, Day, TripDetailDTO } from '../types/trip';
import type { Activity } from '../types/activity';
import { groupItinerariesByDay } from './itineraryConverter';

/**
 * Format date for display
 * @param date Date to format
 * @param formatStr Format string (default: 'PPP' for long date like "December 1, 2025")
 */
export function formatDate(date: Date, formatStr: string = 'PPP'): string {
  return format(date, formatStr, { locale: zhTW });
}

/**
 * 旅程期間，例如「2027年1月12日 – 1月31日」；跨年時兩端都帶年份
 */
export function formatDateRange(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  return `${format(start, 'yyyy年M月d日', { locale: zhTW })} – ${format(end, sameYear ? 'M月d日' : 'yyyy年M月d日', { locale: zhTW })}`;
}

/**
 * Format date as short string (e.g., "12/01")
 */
export function formatDateShort(date: Date): string {
  return format(date, 'MM/dd', { locale: zhTW });
}

/**
 * Format date with day of week (e.g., "12月1日 (週日)")
 */
export function formatDateWithDay(date: Date): string {
  return format(date, 'M月d日 (EEE)', { locale: zhTW });
}

/**
 * Add days to a date
 * @param date Starting date
 * @param days Number of days to add
 */
export function addDays(date: Date, days: number): Date {
  return addDaysFns(date, days);
}

/**
 * Calculate number of days in a trip (inclusive)
 * @param startDate Trip start date
 * @param endDate Trip end date
 */
export function calculateDuration(startDate: Date, endDate: Date): number {
  return differenceInDays(endDate, startDate) + 1; // +1 for inclusive count
}

/**
 * Generate Day objects for a trip with associated activities
 * @param trip Trip entity
 * @param activities All activities for this trip
 */
export function generateDays(trip: Trip, activities: Activity[]): Day[] {
  const durationDays = calculateDuration(trip.startDate, trip.endDate);
  const days: Day[] = [];

  for (let i = 0; i < durationDays; i++) {
    const dayActivities = activities
      .filter((a) => a.tripId === trip.id && a.dayIndex === i)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    days.push({
      dayNumber: i + 1,
      dayIndex: i,
      date: addDays(trip.startDate, i),
      activities: dayActivities,
    });
  }

  return days;
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Parse date string to Date object
 * Handles both ISO strings and date inputs
 */
export function parseDate(dateInput: string | Date): Date {
  if (dateInput instanceof Date) {
    return dateInput;
  }
  return new Date(dateInput);
}

/**
 * Format date for input fields (YYYY-MM-DD)
 */
export function formatDateForInput(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Format a future timestamp as "此連結將在 3 天後失效"-style relative day count.
 * Used by invite link expiry display (day-level granularity only).
 */
export function formatDaysUntil(target: Date): number {
  return Math.max(0, Math.ceil((target.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
}

/**
 * Format a future timestamp as a "剩餘 1 天 14 小時" countdown string.
 * Used by poll expiry countdown (day + hour granularity). Returns null if
 * already past.
 */
export function formatCountdown(target: Date): string | null {
  if (isPast(target)) return null;

  const totalHours = differenceInHours(target, new Date());
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  if (days > 0) return `剩餘 ${days} 天 ${hours} 小時`;
  return `剩餘 ${hours} 小時`;
}

/**
 * Generate Day objects from backend TripDetailDTO with planned itineraries
 * @param tripDetail Backend trip detail response
 */
export function generateDaysFromBackend(tripDetail: TripDetailDTO): Day[] {
  const startDate = new Date(tripDetail.startDate);
  const endDate = new Date(tripDetail.endDate);
  const tripId = String(tripDetail.tripId);
  const durationDays = calculateDuration(startDate, endDate);

  // 將 plannedItineraries 按照 daySequence 分組
  const activitiesByDay = groupItinerariesByDay(
    tripDetail.plannedItineraries || [],
    tripId
  );

  const days: Day[] = [];

  for (let i = 0; i < durationDays; i++) {
    days.push({
      dayNumber: i + 1,
      dayIndex: i,
      date: addDays(startDate, i),
      activities: activitiesByDay.get(i) || [],
    });
  }

  return days;
}
